/**
 * SharedComparisonPage - View a shared property comparison
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Scale, ArrowLeft, Loader2, AlertCircle, 
  Bed, Bath, MapPin, Home, Car, Trees, 
  Check, X, Share2, Copy, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature check icon
const FeatureCheck = ({ has }) => (
  has ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-gray-300" />
);

export default function SharedComparisonPage() {
  const { shareId } = useParams();
  const { country } = useCountry();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const currency = country === 'UK' ? '£' : 'Rs.';
  
  useEffect(() => {
    const fetchComparison = async () => {
      try {
        const response = await fetch(`${API_URL}/api/compare/share/${shareId}`);
        
        if (response.status === 404) {
          setError('Comparison not found');
        } else if (response.status === 410) {
          setError('This comparison link has expired');
        } else if (!response.ok) {
          throw new Error('Failed to load comparison');
        } else {
          const data = await response.json();
          setComparison(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchComparison();
  }, [shareId]);
  
  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading comparison...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-500 mb-6">
            The comparison you're looking for may have been deleted or the link may have expired.
          </p>
          <Link to="/category/property">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse Properties
            </Button>
          </Link>
        </Card>
      </div>
    );
  }
  
  const listings = comparison?.listings || [];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/category/property" className="text-white/80 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <Scale className="w-6 h-6" />
                  <h1 className="text-2xl font-bold">{comparison?.title || 'Property Comparison'}</h1>
                </div>
                <p className="text-blue-100 text-sm mt-1">
                  {listings.length} properties compared • Viewed {comparison?.view_count} times
                </p>
              </div>
            </div>
            
            <Button
              variant="secondary"
              size="sm"
              onClick={copyLink}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>
      
      {/* Comparison Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Property Cards */}
        <div className={`grid gap-6 mb-8`} style={{ gridTemplateColumns: `repeat(${Math.min(listings.length, 4)}, 1fr)` }}>
          {listings.map((listing, idx) => (
            <Card key={listing.id || idx} className="overflow-hidden">
              <img 
                src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'}
                alt={listing.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 line-clamp-2">{listing.title}</h3>
                <p className="text-blue-600 font-bold text-xl mb-2">
                  {currency}{listing.price?.toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span>
                </p>
                <div className="flex items-center gap-1 text-gray-500 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{listing.location}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Comparison Table */}
        <Card className="overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <h2 className="font-semibold text-lg">Detailed Comparison</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="p-3 text-left font-medium text-gray-600 w-48">Feature</th>
                  {listings.map((listing, idx) => (
                    <th key={idx} className="p-3 text-center font-medium text-gray-800 border-l">
                      {listing.title?.substring(0, 20)}...
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Price */}
                <tr className="border-b bg-blue-50">
                  <td className="p-3 font-medium text-gray-700">Price</td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l font-bold text-blue-600">
                      {currency}{listing.price?.toLocaleString()}/mo
                    </td>
                  ))}
                </tr>
                
                {/* Bedrooms */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700 flex items-center gap-2">
                    <Bed className="w-4 h-4" /> Bedrooms
                  </td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l">
                      {listing.bedrooms === 0 ? 'Studio' : listing.bedrooms || '-'}
                    </td>
                  ))}
                </tr>
                
                {/* Bathrooms */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700 flex items-center gap-2">
                    <Bath className="w-4 h-4" /> Bathrooms
                  </td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l">
                      {listing.bathrooms || '-'}
                    </td>
                  ))}
                </tr>
                
                {/* Property Type */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700 flex items-center gap-2">
                    <Home className="w-4 h-4" /> Type
                  </td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l capitalize">
                      {listing.property_type?.replace('_', ' ') || '-'}
                    </td>
                  ))}
                </tr>
                
                {/* Furnishing */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700">Furnishing</td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l capitalize">
                      {listing.furnishing?.replace('_', ' ') || '-'}
                    </td>
                  ))}
                </tr>
                
                {/* Condition */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700">Condition</td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l capitalize">
                      {listing.condition || '-'}
                    </td>
                  ))}
                </tr>
                
                {/* Parking */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700 flex items-center gap-2">
                    <Car className="w-4 h-4" /> Parking
                  </td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l">
                      <FeatureCheck has={listing.features?.includes('parking')} />
                    </td>
                  ))}
                </tr>
                
                {/* Garden */}
                <tr className="border-b">
                  <td className="p-3 font-medium text-gray-700 flex items-center gap-2">
                    <Trees className="w-4 h-4" /> Garden
                  </td>
                  {listings.map((listing, idx) => (
                    <td key={idx} className="p-3 text-center border-l">
                      <FeatureCheck has={listing.features?.includes('garden')} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
        
        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Calendar className="w-4 h-4" />
            Created {new Date(comparison?.created_at).toLocaleDateString()}
          </div>
          <p>This comparison link expires on {new Date(comparison?.expires_at).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}
